import { ExternalLinkIcon, S3Image } from "@kenai/core";
import { useMediaQuery } from "@kenai/core";
import {
  Button,
  ButtonProps,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  Typography,
} from "@mui/material";
import { useCookie } from "next-cookie";
import { FC, useCallback, useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";

export const AgreementLayout: FC = ({ children }) => {
  return (
    <div className="agreement-layout h-full grid text-left py-10 md:py-20 place-content-center place-items-center">
      <div className="container">{children}</div>
    </div>
  );
};

export interface AgreementReaderProps {
  onAgreementContinue?: () => void;
  onAgreementAcceptToggle?: (checked: boolean) => void;
  disabled?: boolean;
  mandatoryRead?: boolean;
  /**
   * Blocks Accept while the agreement body still has something outstanding -
   * an unticked confirmation. Undefined leaves Accept always available, which
   * is what a body with no confirmations of its own wants.
   */
  acceptDisabled?: boolean;
  /** Called when Accept is pressed while `acceptDisabled`, to surface what is outstanding. */
  onAcceptBlocked?: () => void;

  dialogTitle?: string;

  headingText: string;
  description?: string;
  agreementLinkText?: string;

  padding?: boolean;

  companyLogo?: string;
  companyName?: string;
  cookieName: string;
  identifier: string;
  /**
   * Ignore any stored acceptance and make the visitor accept again. For an agreement
   * configured to be re-signed on every visit, where what they accepted before is
   * deliberately not good enough.
   */
  requireAcceptance?: boolean;
  slotProps?: {
    continueButton?: ButtonProps;
  };
}

export const AgreementReader: FC<AgreementReaderProps> = ({
  padding = true,
  slotProps,
  ...props
}) => {
  const [agreementConfirmed, setAgreementConfirmed] = useState(false);
  const cookie = useCookie();
  const agreementCookie = cookie.get<string>(props.cookieName);

  const fullScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [visible, setVisible] = useState(false);
  const [acceptContinue, setAcceptContinue] = useState(false);
  // Acceptance given on THIS visit, which `requireAcceptance` must not undo: it rejects
  // what a previous visit stored, not what the visitor just did in front of us.
  const [acceptedThisVisit, setAcceptedThisVisit] = useState(false);
  const [mandatoryRead, setMandatoryRead] = useState(
    props.mandatoryRead || false
  );

  useEffect(() => {
    setMandatoryRead(props.mandatoryRead || false);
  }, [props.mandatoryRead]);

  /**
   * Acceptance this browser already gave for THIS document.
   *
   * The cookie is written only by an accept that got past the `acceptDisabled` guard, so
   * a cookie carrying this identifier is itself proof that the body's confirmations were
   * satisfied when it was written. It is compared by VALUE because the cookie name is
   * shared across agreements: merely having one would let another entity's acceptance,
   * or an older version's, stand in for this one.
   */
  const storedAcceptance =
    !props.requireAcceptance &&
    ["TEST-MODE", props.identifier].includes(agreementCookie || "");

  useEffect(() => {
    // Accepting clears `mandatoryRead` and sets the cookie, both of which land in this
    // effect's deps, so without this it would run again and `requireAcceptance` would
    // reject the acceptance the visitor had just given.
    if (acceptedThisVisit) {
      return;
    }

    setAgreementConfirmed(
      storedAcceptance || (!mandatoryRead && !props.requireAcceptance)
    );
  }, [
    acceptedThisVisit,
    storedAcceptance,
    props.requireAcceptance,
    mandatoryRead,
  ]);

  const openForAcceptance = () => {
    setAcceptContinue(true);
    setVisible(true);
  };

  const handleContinue = () => {
    // Already accepted, here or on an earlier visit. There is nothing left to ask, and
    // asking anyway would put a dialog in front of someone whose next screen should be
    // the rest of the flow.
    if (storedAcceptance || acceptedThisVisit) {
      props.onAgreementContinue?.();
      return;
    }

    // No acceptance to lean on, so anything the document asks for has to be satisfied
    // first. Consumers that carry no gate leave `acceptDisabled` undefined and reach the
    // last line exactly as before.
    if (props.acceptDisabled || mandatoryRead || props.requireAcceptance) {
      openForAcceptance();
      return;
    }

    props.onAgreementContinue?.();
  };

  const handleAcceptAgreement = () => {
    if (props.acceptDisabled) {
      props.onAcceptBlocked?.();
      return;
    }
    cookie.set(props.cookieName, props.identifier);
    setAcceptedThisVisit(true);
    setAgreementConfirmed(true);
    setMandatoryRead(false);
    setVisible(false);
    if (acceptContinue) {
      props.onAgreementContinue?.();
    }
  };

  const AgreementText = (
    <Typography
      sx={{
        lineHeight: "42px",
      }}
      variant="body1"
      data-cy="accept-agreement"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        setVisible(true);
      }}
    >
      I have read, understood and accepted the{" "}
      <Link
        tabIndex={1}
        role="button"
        onClick={() => setVisible(true)}
        onKeyDown={(e) =>
          e.key === "Enter" || e.code === "Space" ? setVisible(true) : null
        }
        style={{ lineHeight: "inherit" }}
      >
        {props.agreementLinkText || "agreement"}{" "}
        <Typography variant="body1" component="span" noWrap>
          <ExternalLinkIcon sx={{ fontSize: 14 }} />
        </Typography>
      </Link>
    </Typography>
  );

  const handleDenyAgreement = useCallback(() => {
    cookie.remove(props.cookieName);
    setAcceptedThisVisit(false);
    setAgreementConfirmed(false);
    if (props.mandatoryRead) {
      setMandatoryRead(true);
    }
  }, [props.mandatoryRead, agreementConfirmed]);

  return (
    <div>
      {props.companyLogo && (
        <S3Image
          src={props.companyLogo}
          width={200}
          height={100}
          alt={props.companyName}
          className="object-contain object-left"
        />
      )}
      <div className="mb-2 md:mb-4 mt-4">
        <Typography variant="h4" component="h1" data-cy="page-title">
          {props.headingText}
        </Typography>
        {props.description && (
          <Typography variant="subtitle1">{props.description}</Typography>
        )}

        <div className="mt-4">
          {!mandatoryRead || agreementConfirmed ? (
            <FormControlLabel
              control={
                <Checkbox
                  inputProps={
                    {
                      "data-cy": "agreement-checkbox",
                    } as any
                  }
                  onClick={handleDenyAgreement} // only visible when checked
                  checked={agreementConfirmed}
                  disabled={props.disabled}
                />
              }
              label={AgreementText}
            />
          ) : (
            AgreementText
          )}
        </div>
      </div>
      <div className="action-container pt-8">
        <Button
          variant="contained"
          onClick={handleContinue}
          data-cy="next-step"
          tabIndex={2}
          {...slotProps?.continueButton}
        >
          Continue
        </Button>
      </div>
      <Dialog open={visible} fullScreen={fullScreen} maxWidth="lg">
        <DialogTitle>
          {/* No fallback. A caller that has a title passes one, and the signed copy and
              the kiosk both print nothing where there is none to print. */}
          {props.dialogTitle}
          <IconButton
            aria-label="close"
            onClick={() => setVisible(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={!padding ? { padding: 0 } : undefined}>
          {props.children}
          {padding && <div className="mb-20" aria-hidden />}
          <div className="absolute bottom-0 right-0 z-10 pr-8 pb-5">
            {/* Accept, not Continue, while a confirmation is still outstanding. A
                returning visitor arrives already confirmed, and Continue would take
                them to a handler that reopens an already-open dialog and says
                nothing, so the red highlight never appears. */}
            {agreementConfirmed && !props.acceptDisabled ? (
              <Button
                size="large"
                variant="contained"
                onClick={handleContinue}
                data-cy="next-step"
                {...slotProps?.continueButton}
              >
                Continue
              </Button>
            ) : (
              <Button
                size="large"
                variant="contained"
                onClick={handleAcceptAgreement}
                data-cy="accept-agreement-button"
                {...slotProps?.continueButton}
              >
                Accept
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
